import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { COMPANY, IMAGES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden blueprint-bg"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
        aria-hidden="true"
      >
        <img
          src={IMAGES.hero}
          alt="Industrial packaging robotics"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-obsidian/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/80" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 min-h-screen flex flex-col justify-end pb-16 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-spartanorange" />
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-white/70">
            {COMPANY.legal} · Shah Alam, MY
          </span>
        </motion.div>

        <h1 className="font-display uppercase tracking-tighter text-white leading-[0.82] text-7xl sm:text-8xl lg:text-[12rem]">
          <MaskLine delay={0.35}>Spartan</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-xl">
            <MaskLine delay={0.75} className="font-sans text-lg md:text-2xl text-white/85">
              {COMPANY.tagline}.
            </MaskLine>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1 }}
              className="mt-4 text-sm md:text-base text-white/60 leading-relaxed"
            >
              End-to-end packaging machinery — from primary fill to robotic
              palletizing. Sourced from the world&apos;s best, integrated for your floor.
            </motion.p>
          </div>

          <motion.a
            href="#contact"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 self-start md:self-auto bg-spartanorange text-obsidian font-mono text-sm tracking-[0.15em] uppercase px-8 py-5 hover:bg-white transition-colors duration-200"
          >
            Contact Us — No Obligation
            <ArrowDownRight
              size={20}
              className="transition-transform duration-200 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </motion.a>
        </div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 right-6 md:right-16 z-10 hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40"
      >
        Scroll <span className="h-8 w-px bg-white/30 animate-pulse" />
      </motion.div>
    </section>
  );
}
